import { h } from "preact";
import { forwardRef } from "preact/compat";

import "./OnAir.scss";
import NowPlaying from "../NowPlaying/NowPlaying";
import { useEffect, useLayoutEffect, useRef, useMemo } from "preact/hooks";
import { useQuery } from "@urql/preact";
import { DateTime } from "luxon";
import { useEffectEveryHourOnTheHour } from "../../lib/useEffectEveryHourOnTheHour";

const CurrentShowQuery = `
    query {
        currentAndNext {
            current {
              ... on Timeslot{
                title
                description
                season {
                  credits {
                    User {
                      fname
                      sname
                    }
                  }
                  show {
                    photo
                  }
                }
                startTime
                endTime
                webpage
              }
              ... on CurrentAndNext_PseudoTimeslot {
                title
                desc
                photo
                start_time
                end_time
              }
            }
          }
    }
`;

interface CurrentShowInfo {
  currentAndNext: {
    current:
      | {
          title: string;
          description: string;
          season: {
            credits: [{ User: { fname: string; sname: string } }];
            show: { photo: string | null };
          };
          startTime: string;
          endTime: string;
          webpage: string;
        }
      | {
          title: string;
          desc: string;
          photo: string;
          start_time: string | null;
          end_time: string;
        };
  };
}

const OnAir = forwardRef<HTMLDivElement>((props, ref) => {
  const [result, refetch] = useQuery<CurrentShowInfo>({
    query: CurrentShowQuery,
    requestPolicy: 'network-only'
  });
  useEffectEveryHourOnTheHour(refetch);
  const endTime = useMemo(() => {
    if (!result.data) {
      return null;
    }
    const show = result.data!.currentAndNext.current;
    const time = "endTime" in show ? show.endTime : show.end_time;
    if (time === "The End of Time") {
      return time;
    }
    let datetime: DateTime;
    if (/^\d+$/.test(time)) {
      datetime = DateTime.fromSeconds(parseInt(time, 10));
    } else {
      datetime = DateTime.fromISO(time);
    }
    const now = DateTime.local();
    const diff = datetime.diff(now, "days").toObject().days
    return datetime.toLocaleString(
        diff < 1 ? DateTime.TIME_24_SIMPLE : DateTime.DATETIME_SHORT
    );
    return datetime.toLocaleString(DateTime.TIME_24_SIMPLE);
  }, [result.data]);
  if (result.fetching) {
    return (
      <div class="on-air-container" ref={ref}>
        <div class="on-air on-air-main" >
          <div class="on-air-meta">
            <span class="label-container">
              <span class="label">ON NOW</span>
            </span>
            <div className="loading">Loading...</div>
          </div>
        </div>
      </div>
    );
  }
  const show = result.data!.currentAndNext.current;
  return (
    <div ref={ref} title={"On Air: " + show.title}>
      <div class={"on-air-container" + ("webpage" in show ? " link" : "")} onClick={() => "webpage" in show ? window.open("https://ury.org.uk" + show.webpage, "_blank") : ""}>
      <div class="on-air-main">
        <img
          alt={"Show image for " + show.title}
          src={"https://ury.org.uk" + ("photo" in show ? show.photo : show.season.show.photo)}
        />
        <div class="on-air-meta">
          <span class="label-container">
            <span class="label">ON NOW</span> until {endTime}
          </span>
          <span class="title">{show.title}</span>
          {"season" in show && (
            <span class="people">
              with{" "}
              {show.season.credits
                .map((x) => x.User.fname + " " + x.User.sname)
                .join(", ")}
            </span>
          )}
          {"webpage" in show ? (
            <a href={"https://ury.org.uk" + show.webpage} target="_blank" class="button">Full show info...</a>
          ) : ""}
        </div>
      </div>

      <NowPlaying />

      <span>
        {"desc" in show ? (
          <p class="desc">{show.desc}</p>
        ) : (
          <div
            class="desc"
            dangerouslySetInnerHTML={{ __html: show.description }}
          />
        )}

      </span>

    </div>
    </div>
  );
});

export default OnAir;
